import {
    ChevronLeft,
    ChevronRight,
    Search,
    UsersRound,
    X,
} from "lucide-react";
import {
    ChangeEvent,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { getMyReferrals } from "../../../../Redux/Teams/slice";
import { AppDispatch, RootState } from "../../../../store";
import { mapReferralToInvitationRecord } from "./MyInvitationsData";

import "./MyInvitationsPage.css";

const PAGE_SIZE = 10;

const MyInvitationsPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { referralMembers = [], loading } = useSelector(
        (state: RootState) => state.teams
    );
    const invitationRecords = useMemo(
        () => referralMembers.map(mapReferralToInvitationRecord),
        [referralMembers]
    );

    const [
        searchValue,
        setSearchValue,
    ] =
        useState("");

    const [
        currentPage,
        setCurrentPage,
    ] =
        useState(1);

    useEffect(() => {
        dispatch(getMyReferrals());
    }, [dispatch]);

    const filteredRows = useMemo(() => {
        const search = searchValue.trim().toLowerCase();

        if (!search) {
            return invitationRecords;
        }

        return invitationRecords.filter((record) =>
            record.searchText.includes(search)
        );
    }, [invitationRecords, searchValue]);

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredRows.length /
                PAGE_SIZE
            )
        );

    useEffect(
        () => {
            setCurrentPage(
                (
                    previous
                ) =>
                    Math.min(
                        previous,
                        totalPages
                    )
            );
        },
        [
            totalPages,
        ]
    );

    const visibleRows =
        useMemo(
            () => {
                const start =
                    (
                        currentPage -
                        1
                    ) *
                    PAGE_SIZE;

                return filteredRows.slice(
                    start,
                    start +
                    PAGE_SIZE
                );
            },
            [
                filteredRows,
                currentPage,
            ]
        );

    const pageNumbers =
        useMemo(
            () =>
                Array.from(
                    {
                        length:
                            totalPages,
                    },
                    (
                        _,
                        index
                    ) =>
                        index +
                        1
                ),
            [
                totalPages,
            ]
        );

    const handleSearch =
        (
            event: ChangeEvent<HTMLInputElement>
        ) => {
            setSearchValue(
                event.target.value
            );

            setCurrentPage(
                1
            );
        };

    const clearSearch =
        () => {
            setSearchValue(
                ""
            );

            setCurrentPage(
                1
            );
        };

    return (
        <section className="my-invitations-page">
            <div className="my-invitations-shell">
                <div className="my-invitations-card">
                    <header className="my-invitations-header">
                        <div className="my-invitations-heading">
                            <span className="my-invitations-icon">
                                <UsersRound
                                    size={
                                        19
                                    }
                                    strokeWidth={
                                        1.9
                                    }
                                />
                            </span>

                            <div>
                                <span className="my-invitations-kicker">
                                    MY NETWORK
                                </span>

                                <h1>
                                    My Invitations
                                </h1>

                                <p>
                                    View people who joined your partner network.
                                </p>
                            </div>
                        </div>

                        <div className="my-invitations-search">
                            <Search
                                size={
                                    16
                                }
                                strokeWidth={
                                    1.9
                                }
                            />

                            <input
                                type="text"
                                inputMode="search"
                                value={
                                    searchValue
                                }
                                onChange={
                                    handleSearch
                                }
                                placeholder="Search invitations"
                                aria-label="Search invitations"
                            />

                            {searchValue && (
                                <button
                                    type="button"
                                    onClick={
                                        clearSearch
                                    }
                                    aria-label="Clear search"
                                >
                                    <X
                                        size={
                                            14
                                        }
                                        strokeWidth={
                                            2.2
                                        }
                                    />
                                </button>
                            )}
                        </div>
                    </header>

                    <div className="my-invitations-summary">
                        <span>
                            Total Invitations
                        </span>

                        <strong>
                            {
                                filteredRows.length
                            }
                        </strong>
                    </div>

                    <div className="my-invitations-table-shell">
                        <div
                            className="my-invitations-table-scroll"
                            role="region"
                            aria-label="My invitations table"
                            tabIndex={
                                0
                            }
                        >
                            <table className="my-invitations-table">
                                <thead>
                                    <tr>
                                        <th
                                            scope="col"
                                            className="serial-column"
                                        >
                                            S.No
                                        </th>

                                        <th
                                            scope="col"
                                        >
                                            Date of Joining
                                        </th>

                                        <th
                                            scope="col"
                                        >
                                            Partner Network ID
                                        </th>

                                        <th
                                            scope="col"
                                        >
                                            Partner Network Name
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td
                                                colSpan={
                                                    4
                                                }
                                                className="my-invitations-empty"
                                            >
                                                <strong>
                                                    Loading...
                                                </strong>
                                            </td>
                                        </tr>
                                    ) : visibleRows.length >
                                        0 ? (
                                        visibleRows.map(
                                            (
                                                record,
                                                index
                                            ) => (
                                                <tr
                                                    key={
                                                        record.id
                                                    }
                                                >
                                                    <td className="serial-column">
                                                        {
                                                            (
                                                                currentPage -
                                                                1
                                                            ) *
                                                            PAGE_SIZE +
                                                            index +
                                                            1
                                                        }
                                                    </td>

                                                    <td>
                                                        <span className="table-cell-primary">
                                                            {
                                                                record.dateOfJoining
                                                            }
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <span className="network-id-badge">
                                                            {
                                                                record.partnerNetworkId
                                                            }
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <span className="table-cell-primary">
                                                            {
                                                                record.partnerNetworkName
                                                            }
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={
                                                    4
                                                }
                                                className="my-invitations-empty"
                                            >
                                                <UsersRound
                                                    size={
                                                        26
                                                    }
                                                    strokeWidth={
                                                        1.7
                                                    }
                                                />

                                                <strong>
                                                    No invitations found
                                                </strong>

                                                <span>
                                                    Try a different search term.
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <footer className="my-invitations-footer">
                        <span className="my-invitations-result-count">
                            Showing{" "}
                            <strong>
                                {visibleRows.length >
                                    0
                                    ? (
                                        currentPage -
                                        1
                                    ) *
                                    PAGE_SIZE +
                                    1
                                    : 0}
                            </strong>{" "}
                            -{" "}
                            <strong>
                                {Math.min(
                                    currentPage *
                                    PAGE_SIZE,
                                    filteredRows.length
                                )}
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {
                                    filteredRows.length
                                }
                            </strong>
                        </span>

                        <div className="my-invitations-pagination">
                            <button
                                type="button"
                                className="pagination-arrow"
                                disabled={
                                    currentPage ===
                                    1
                                }
                                onClick={() =>
                                    setCurrentPage(
                                        (
                                            previous
                                        ) =>
                                            Math.max(
                                                1,
                                                previous -
                                                1
                                            )
                                    )
                                }
                                aria-label="Previous page"
                            >
                                <ChevronLeft
                                    size={
                                        16
                                    }
                                    strokeWidth={
                                        2
                                    }
                                />
                            </button>

                            {pageNumbers.map(
                                (
                                    page
                                ) => (
                                    <button
                                        type="button"
                                        className={`pagination-page ${currentPage ===
                                                page
                                                ? "active"
                                                : ""
                                            }`}
                                        key={
                                            page
                                        }
                                        onClick={() =>
                                            setCurrentPage(
                                                page
                                            )
                                        }
                                        aria-current={
                                            currentPage ===
                                                page
                                                ? "page"
                                                : undefined
                                        }
                                    >
                                        {
                                            page
                                        }
                                    </button>
                                )
                            )}

                            <button
                                type="button"
                                className="pagination-arrow"
                                disabled={
                                    currentPage ===
                                    totalPages
                                }
                                onClick={() =>
                                    setCurrentPage(
                                        (
                                            previous
                                        ) =>
                                            Math.min(
                                                totalPages,
                                                previous +
                                                1
                                            )
                                    )
                                }
                                aria-label="Next page"
                            >
                                <ChevronRight
                                    size={
                                        16
                                    }
                                    strokeWidth={
                                        2
                                    }
                                />
                            </button>
                        </div>
                    </footer>
                </div>
            </div>
        </section>
    );
};

export default MyInvitationsPage;
